import React, { FC, useEffect, useMemo } from 'react';

import { Stack } from '@mui/system';
import { FormikValues, useFormik } from 'formik';

import {
  AdminLegendVariant,
  adminResetValue,
  AllowanceEnums,
  ContractTypes,
  getLongTermContractAttributes,
  HelpTypes,
  IAdminSearchResponse,
  IAdminWorkshiftDetails,
  isIncompatbileMatchSelector,
  ProfessionalReplacementTypes,
  searchedPharmacies,
  searchedPharmacists,
  TypeOfUser,
  validateExpressBooking,
  WorkshiftStatus,
} from '@pharmaplan/common';
import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import strings from '../../../localization';
import CustomTextField from '../../common/CustomTextField';
import genericClasses from '../../../theme/GenericClasses';
import CustomAutocomplete from '../../common/CustomAutocomplete';
import {
  rateLongTermParamsValidation,
  rateValidation,
  toStandardFormat,
} from '../../../helpers/Functions';
import {
  ContractTypesDropdown,
  OtherScreens,
  ReplacementType,
} from '../../../helpers/Constants';
import {
  IFromScreen,
  renderScreen,
  setBack,
  setFooter,
} from '../../../actions/drawerActions';
import CustomCheckbox from '../../common/CustomCheckbox';
import useAutoCompleteScroll, {
  AutoCompleteScrollTypes,
} from '../../../hooks/useAutoCompleteScroll';

import useExpressBookingDisabled from '../../../hooks/Admin/ExpressBooking/useExpressBookingDisabled';
import useExpressBookingHr from '../../../hooks/Admin/ExpressBooking/useExpressBookingHr';
import useExpressBooking from '../../../hooks/Admin/ExpressBooking/useExpressBooking';
import useSyncWorkshiftValues from '../../../hooks/workshift/useSyncWorkshiftValues';
import { getDisableAvailability } from '../../../selectors/calendarSelector';
import CustomDropdown from '../../common/CustomDropdown';
import useSelfService from '../../../hooks/useSelfService';
import { getDayMonthDateYear } from '../../Reports/helpers';
import useSyncMultiHours from '../../../hooks/workshift/useSyncMultiHours';
import useExpressBookingPromos from '../../../hooks/Admin/ExpressBooking/useExpressBookingPromos';

import Validator, { SelfExpressBookingValidator } from './validator';
import ExpressBookingAllowances from './ExpressBookingAllowances';
import ExpressBookingDuration from './ExpressBookingDuration';
import { checkboxConfig, notesConfig } from './helper';
import styles from './styles';

const resetVal = 'isIncompatibleMatch';
interface IExpressBooking {
  id?: string;
  workshiftId?: string;
  type: Exclude<AdminLegendVariant, AdminLegendVariant.total>;
  fromScreen?: IFromScreen;
  openedDate?: string;
}

const { pharmacist, pharmacy } = TypeOfUser;
const { AdminBookingReports, ListOfWorkshifts, AdminPostedWorkshiftReports } = OtherScreens;
const { meal, travel, emergency, accommodation, others } = AllowanceEnums;
const replacementHelpType = {
  [ProfessionalReplacementTypes.pharmacist]: HelpTypes.pharmacist,
  [ProfessionalReplacementTypes.student]: HelpTypes.students,
  [ProfessionalReplacementTypes.technician]: HelpTypes.technicians,
};

export const toOptions = (userSearch: IAdminSearchResponse[]) =>
  userSearch.map((item) => {
    const { userName, email, id } = item ?? {};
    return {
      label: userName,
      subtitle: email,
      id,
    };
  });

const noActionsScreens = [
  AdminBookingReports,
  AdminPostedWorkshiftReports,
  ListOfWorkshifts,
];

const ExpressBooking: FC<IExpressBooking> = ({
  id,
  type,
  openedDate,
  fromScreen,
  workshiftId,
}) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();

  const submitDisabled = useAppSelector(getDisableAvailability);
  const pharmacists = useAppSelector(searchedPharmacists);
  const pharmacies = useAppSelector(searchedPharmacies);

  const pharmacistOptions = toOptions(pharmacists);
  const pharmacyOptions = toOptions(pharmacies);
  const noActionsScreen = noActionsScreens.includes(fromScreen as OtherScreens);
  const longTermAttributes = useAppSelector(getLongTermContractAttributes);

  const {
    debouncedInput,
    getFormikInitialValues,
    debouncedPharmacyInput,
    confirmAction,
    detailsApiMap,
    bookWorkshift,
    bookLongTerm,
  } = useExpressBooking(type, id, noActionsScreen);

  const formikInitialValues = getFormikInitialValues();
  const isIncompatibleMatch = useAppSelector(isIncompatbileMatchSelector);

  const isBooking = type === AdminLegendVariant.booked;
  const isEventFromCalendar = !!id;

  const { details } = detailsApiMap?.[type] ?? {};
  const {
    travelAllowanceTypes,
    accommodationAllowanceType,
    mealAllowance,
    emergencyFees,
    pharmacyId,
    otherAllowance,
    replacementType,
  } = (details as unknown as IAdminWorkshiftDetails) ?? {};

  const clientId = details[idStore.id as keyof typeof details];

  const handleSubmit = (values: FormikValues) => {
    const {
      pharmacistHourlyRate,
      pharmacyHourlyRate,
      contractType,
      creditsLeft,
      professionalCreditsLeft,
    } = values ?? {};
    const isLongTerm = contractType === ContractTypes.longTerm;
    let pharmacyGreaterPharmacist = true;

    if (isLongTerm) {
      pharmacyGreaterPharmacist = rateLongTermParamsValidation({
        professionalWeekday: values?.weekDayPaidHourlyRate,
        professionalWeekend: values?.weekendPaidHourlyRate,
        pharmacyWeekday: values?.pharmacyWeekDayHourlyRate,
        pharmacyWeekend: values?.pharmacyWeekendHourlyRate,
        dispatch,
        isSelfService:
          isSelfService || !!creditsLeft || !!professionalCreditsLeft,
        weekdayHours: values?.weekdayHours,
        weekendHours: values?.weekendHours,
        replacementType: values?.replacementType,
      });
    } else {
      pharmacyGreaterPharmacist = rateValidation({
        pharmacistRate: pharmacistHourlyRate,
        pharmacyRate: pharmacyHourlyRate,
        isSelfService:
          isSelfService || !!creditsLeft || !!professionalCreditsLeft,
        dispatch,
        replacementType: values?.replacementType,
      });
    }

    if (pharmacyGreaterPharmacist) {
      dispatch(
        validateExpressBooking({
          pharmacistId: values.pharmacistId,
          pharmacyId: values.pharmacyId,
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      notesForSalesOrder: '',
      sendEmailToPharmacist: false,
      sendEmailToPharmacy: false,
      displayDate: '',
      ...formikInitialValues,
    },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: isSelfService
      ? SelfExpressBookingValidator()
      : Validator(),
  });

  const rType = id ? replacementType : formik.values?.replacementType;
  const helpUserType = replacementHelpType[
    (rType as keyof typeof replacementHelpType)
        ?? ProfessionalReplacementTypes.pharmacist
  ];
  const isLongTerm = formik.values.contractType === ContractTypes.longTerm;
  const isEditingMultiDayBooking = isEventFromCalendar && isBooking && isLongTerm;
  const { pharmacists: formikPharmacists, pharmacies: formikPharmacies } = formik.values;
  const { onEndReached, pharmacistListData, pharmacyListData } = useAutoCompleteScroll(AutoCompleteScrollTypes.all, helpUserType);

  const onPharmacistSelect = (pId: string) => {
    if (isEditingMultiDayBooking) return;

    const isSelectedPharmacistSame = pId === (details?.[idStore.id as keyof typeof details] as string);

    if (
      type === AdminLegendVariant.requested
      && isLongTerm
      && formik.values?.workshiftIds?.length > 0
      && !isSelectedPharmacistSame
    ) {
      formik.setFieldValue('workshiftIds', []);
    }

    if (isSelectedPharmacistSame) {
      const workshiftIds = details?.allShifts
        ?.filter((item) =>
          item.status === WorkshiftStatus.Requested)
        .map((item) =>
          item.workShiftId);
      formik.setFieldValue('workshiftIds', workshiftIds);
    }
  };

  const clearProfessional = (value: string) => {
    if (value === String(formik.values.replacementType)) return;

    formik.setFieldValue('pharmacists', '');
    formik.setFieldValue('pharmacistSelected', null);
    formik.setFieldValue('pharmacistId', '');
  };

  useExpressBookingDisabled(formik, formik.values.pharmacistId, isBooking);

  const onlyAvailableAllShifts = useMemo(
    () =>
      (isEditingMultiDayBooking ? [] : details?.allShifts)?.filter(
        (item) =>
          item.status !== WorkshiftStatus.Booked,
      ),
    [details?.allShifts, isEditingMultiDayBooking],
  );

  useEffect(() => {
    const longTermDisableCriteria = () => {
      if (isEditingMultiDayBooking) {
        return !detailsApiMap[AdminLegendVariant.booked].details.bookings
          ?.length;
      }
      return !formik.values?.workshiftIds?.length && isLongTerm && !!id;
    };

    const back = () => {
      dispatch(
        renderScreen({
          eventId: workshiftId,
          ...detailsApiMap[type].back,
          date: detailsApiMap[type].details.startDate,
        }),
      );
    };

    if (id && !noActionsScreen) {
      dispatch(setBack(back));
    }

    dispatch(
      setFooter({
        show: true,
        pagination: {
          show: false,
        },
        buttons: {
          show: true,
          primaryButton: {
            label: strings.saveBooking,
            onClick: formik.handleSubmit,
            disabled: longTermDisableCriteria(),
          },
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikInitialValues.pharmacyId, formik.values, submitDisabled]);

  const handleIncompatibleMatch = () => {
    const bookShift = isLongTerm ? bookLongTerm : bookWorkshift;
    if (isIncompatibleMatch) {
      confirmAction(() =>
        bookShift(formik.values));
    } else {
      bookShift(formik.values);
    }
    dispatch(adminResetValue([resetVal]));
  };

  useEffect(() => {
    if (isIncompatibleMatch !== null) {
      handleIncompatibleMatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncompatibleMatch]);

  useEffect(() => {
    debouncedInput(formik.values.pharmacists, helpUserType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput, formik.values.pharmacists]);

  useEffect(() => {
    if (!id) {
      debouncedPharmacyInput(formik.values.pharmacies);
    }
  }, [debouncedPharmacyInput, formik.values.pharmacies, id]);

  useEffect(() => {
    const { startDate, endDate } = longTermAttributes ?? {};
    if (startDate && endDate) {
      const dateString = `${getDayMonthDateYear(toStandardFormat(startDate))} ${
        strings.to
      } ${getDayMonthDateYear(toStandardFormat(endDate))}`;

      formik.setFieldValue('displayDate', dateString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [longTermAttributes]);

  const pharmacyOption = formikPharmacies ? pharmacyOptions : pharmacyListData;
  const pharmacistOption = formikPharmacists
    ? pharmacistOptions
    : pharmacistListData;

  useEffect(() => {
    formik.setFieldValue(meal, !!mealAllowance);
    formik.setFieldValue(travel, !!travelAllowanceTypes?.length);
    formik.setFieldValue(accommodation, !!accommodationAllowanceType);
    formik.setFieldValue(emergency, !!emergencyFees);
    formik.setFieldValue(others, !!otherAllowance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  useSyncMultiHours(formik);
  useSyncWorkshiftValues(formik);
  useExpressBookingHr(
    formik,
    id ?? '',
    pharmacyId,
    isLongTerm,
    clientId as string,
    isBooking,
    details as IAdminWorkshiftDetails,
  );
  useExpressBookingPromos(formik, details, isBooking, id);

  return (
    <Stack sx={styles.container}>
      <CustomAutocomplete
        label={strings.pharmacy}
        onEndReached={onEndReached(pharmacy)}
        infiniteScroll={!formikPharmacies}
        idName="pharmacyId"
        textName="pharmacies"
        autoCompleteName="pharmacySelected"
        placeholder={
          strings.formatString(strings.selectUser, strings.pharmacy) as string
        }
        disabled={isEventFromCalendar}
        formik={formik}
        options={pharmacyOption}
      />

      <CustomDropdown
        formik={formik}
        label={strings.contractType}
        name="contractType"
        disabled={!!id}
        menuItems={ContractTypesDropdown()}
        customClass={genericClasses.borderNoneTextField}
      />
      <ExpressBookingDuration
        isLongTerm={isLongTerm}
        formik={formik}
        id={id}
        allShiftsLength={onlyAvailableAllShifts?.length}
        openedDate={openedDate}
        type={type}
      />

      <CustomDropdown
        formik={formik}
        label={strings.typeReplacement}
        name="replacementType"
        disabled={!!id}
        onClickMenuItem={clearProfessional}
        menuItems={ReplacementType()}
        customClass={genericClasses.borderNoneTextField}
      />

      <CustomAutocomplete
        label={strings.professional}
        idName="pharmacistId"
        customHandler={onPharmacistSelect}
        onEndReached={() =>
          onEndReached(pharmacist)}
        infiniteScroll={!formikPharmacists}
        textName="pharmacists"
        autoCompleteName="pharmacistSelected"
        placeholder={
          strings.formatString(
            strings.selectUser,
            strings.professional,
          ) as string
        }
        formik={formik}
        options={pharmacistOption}
      />

      <ExpressBookingAllowances
        isLongTerm={isLongTerm}
        isUpdate={!!id}
        isEditingBooking={isEditingMultiDayBooking}
        allShifts={isEditingMultiDayBooking ? [] : (details?.allShifts ?? [])}
        formik={formik}
      />

      {notesConfig(isLongTerm).map((item) => {
        const { name, label } = item ?? {};
        return (
          <CustomTextField
            key={name}
            variant="outlined"
            formik={formik}
            id={name}
            placeholder=" "
            label={label}
            name={name}
            multiline
            maxLength={200}
            customClass={genericClasses.borderNoneTextField}
          />
        );
      })}

      {checkboxConfig().map((item) => {
        const { name, label, key } = item ?? {};
        return (
          <CustomCheckbox
            key={key}
            labelClass={styles.checkboxLabel}
            value={formik.values[name as keyof typeof formik.values] as boolean}
            name={name}
            label={label}
            id=""
            handleChange={(event) =>
              formik.setFieldValue(name, event.target.checked)}
          />
        );
      })}
    </Stack>
  );
};

export default ExpressBooking;
